import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["enabledToggle", "panel", "locationList"];

  static values = {
    initial: Object,
    staffRoles: Array,
  };

  connect() {
    this.locations = [];
    this.parseInitial();
    this.render();
    this.bindRoomChanges();
  }

  disconnect() {
    if (this.roomChangeHandler) {
      document.removeEventListener("room-booking:rooms-changed", this.roomChangeHandler);
    }
  }

  parseInitial() {
    const initial = this.initialValue || {};
    this.enabled = !!initial.enabled;
    this.locationState = {};

    (initial.locations || []).forEach((location) => {
      this.locationState[location.id] = {
        id: location.id,
        name: location.name,
        selected: !!location.selected,
        requirements: location.requirements || [],
      };
    });

    if (this.hasEnabledToggleTarget) {
      this.enabledToggleTarget.checked = this.enabled;
    }
    this.updatePanelVisibility();
  }

  bindRoomChanges() {
    this.roomChangeHandler = (event) => {
      this.syncLocationsFromRooms(event.detail.locations);
    };
    document.addEventListener("room-booking:rooms-changed", this.roomChangeHandler);
  }

  syncLocationsFromRooms(roomLocations) {
    roomLocations.forEach((location) => {
      if (!this.locationState[location.id]) {
        this.locationState[location.id] = {
          id: location.id,
          name: location.name,
          selected: false,
          requirements: [],
        };
      } else {
        this.locationState[location.id].name = location.name;
      }
    });

    const activeIds = roomLocations.map((location) => location.id);
    Object.keys(this.locationState).forEach((id) => {
      if (!activeIds.includes(parseInt(id, 10))) {
        delete this.locationState[id];
      }
    });

    this.render();
  }

  toggleEnabled() {
    this.enabled = this.enabledToggleTarget.checked;
    this.updatePanelVisibility();
  }

  updatePanelVisibility() {
    if (!this.hasPanelTarget) return;
    this.panelTarget.classList.toggle("d-none", !this.enabled);
  }

  toggleLocation(event) {
    const locationId = parseInt(event.target.dataset.locationId, 10);
    const state = this.locationState[locationId];
    if (!state) return;

    state.selected = event.target.checked;
    if (!state.selected) {
      state.requirements = [];
    }
    this.render();
  }

  toggleStaffRole(event) {
    const locationId = parseInt(event.target.dataset.locationId, 10);
    const roleId = parseInt(event.target.dataset.roleId, 10);
    const state = this.locationState[locationId];
    if (!state) return;

    if (event.target.checked) {
      if (!state.requirements.find((req) => req.staff_role_id === roleId)) {
        state.requirements.push({ staff_role_id: roleId, quantity: 1 });
      }
    } else {
      state.requirements = state.requirements.filter((req) => req.staff_role_id !== roleId);
    }
    this.render();
  }

  updateQuantity(event) {
    const locationId = parseInt(event.target.dataset.locationId, 10);
    const roleId = parseInt(event.target.dataset.roleId, 10);
    const quantity = parseInt(event.target.value, 10) || 1;
    const state = this.locationState[locationId];
    if (!state) return;

    const requirement = state.requirements.find((req) => req.staff_role_id === roleId);
    if (requirement) {
      requirement.quantity = Math.max(1, quantity);
    }
  }

  render() {
    if (!this.hasLocationListTarget) return;

    const locations = Object.values(this.locationState).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    if (locations.length === 0) {
      this.locationListTarget.innerHTML =
        '<p class="rb-field-hint mb-0">Select at least one room to choose locations.</p>';
      return;
    }

    this.locationListTarget.innerHTML = locations
      .map((location) => this.locationTemplate(location))
      .join("");
  }

  locationTemplate(location) {
    const roles = this.staffRolesValue || [];
    const roleFields = roles
      .map((role) => {
        const requirement = location.requirements.find((req) => req.staff_role_id === role.id);
        const checked = !!requirement;
        const quantity = requirement ? requirement.quantity : 1;
        const hiddenClass = location.selected && checked ? "" : "d-none";

        return `
          <div class="rb-open-shift__role">
            <label class="rb-open-shift__role-check">
              <input type="checkbox"
                     ${checked ? "checked" : ""}
                     ${location.selected ? "" : "disabled"}
                     data-location-id="${location.id}"
                     data-role-id="${role.id}"
                     data-action="change->open-shift-form#toggleStaffRole">
              <span>${this.escape(role.name)}</span>
            </label>
            <div class="rb-open-shift__quantity ${hiddenClass}">
              <label class="sr-only" for="qty-${location.id}-${role.id}">Quantity</label>
              <input type="number"
                     id="qty-${location.id}-${role.id}"
                     name="open_shift[locations][${location.id}][${role.id}]"
                     value="${quantity}"
                     min="1"
                     class="form-control form-control-sm rb-open-shift__quantity-input"
                     data-location-id="${location.id}"
                     data-role-id="${role.id}"
                     data-action="input->open-shift-form#updateQuantity"
                     ${checked && location.selected ? "" : "disabled"}>
            </div>
          </div>
        `;
      })
      .join("");

    return `
      <div class="rb-open-shift__location">
        <label class="rb-open-shift__location-check">
          <input type="checkbox"
                 name="open_shift[location_ids][]"
                 value="${location.id}"
                 ${location.selected ? "checked" : ""}
                 data-location-id="${location.id}"
                 data-action="change->open-shift-form#toggleLocation">
          <span class="rb-open-shift__location-name">${this.escape(location.name)}</span>
        </label>
        <div class="rb-open-shift__roles ${location.selected ? "" : "d-none"}">
          <span class="rb-label">Staff types required</span>
          ${roleFields}
        </div>
      </div>
    `;
  }

  escape(value) {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }
}
